import { Field, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Fragment, FunctionComponent, ReactNode } from 'react';
import { useController } from 'react-hook-form';
import { ErrorMessage } from 'softcat:components/error-message.tsx';
import { Form } from 'softcat:components/form.tsx';

type FormListboxProperties = {
    label: ReactNode;
    name: string;
    options: { label: string; value: string }[];
    placeholder: string | ReactNode;
};

export const FormListbox: FunctionComponent<FormListboxProperties> = ({ label, name, options, placeholder }) => {
    const { field, fieldState } = useController({ name });

    return (
        <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4">
            <Field as={Fragment}>
                <Form.Label htmlFor={name}>{label}</Form.Label>

                <div className={'mt-2 sm:col-span-3 sm:mt-0'}>
                    <Listbox {...field}>
                        <div className="relative">
                            <ListboxButton
                                className={clsx(
                                    'relative w-full cursor-default rounded-md border-0 bg-white py-1.5 pl-3 pr-8 text-left text-xs shadow-sm ring-1 focus:outline-none focus:ring-2 sm:leading-6',
                                    {
                                        'text-gray-900 ring-gray-200 focus:ring-indigo-600': !fieldState.error,
                                        'text-red-600 ring-red-600 focus:ring-red-600': fieldState.error,
                                    },
                                )}
                            >
                                <span className="block truncate">
                                    {options.find((option) => {
                                        return option.value === field.value;
                                    })?.label ||
                                        field.value ||
                                        placeholder}
                                </span>

                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                        aria-hidden="true"
                                        className={clsx('h-5 w-5', {
                                            'text-gray-400': !fieldState.error,
                                            'text-red-600': fieldState.error,
                                        })}
                                    />
                                </span>
                            </ListboxButton>

                            <ListboxOptions
                                className="absolute z-10 mt-1 max-h-36 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                                transition
                            >
                                {options.map((option) => {
                                    return (
                                        <ListboxOption
                                            className="group relative cursor-default select-none py-2 pl-3 pr-9 text-xs text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white"
                                            key={option.value}
                                            value={option.value}
                                        >
                                            <span className="block truncate font-normal group-data-[selected]:font-semibold">{option.label}</span>

                                            <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-[selected]:flex group-data-[focus]:text-white">
                                                <CheckIcon aria-hidden="true" className="size-4" />
                                            </span>
                                        </ListboxOption>
                                    );
                                })}
                            </ListboxOptions>
                        </div>
                    </Listbox>

                    {fieldState.error && (
                        <div className="mt-3">
                            <ErrorMessage>{fieldState.error.message}</ErrorMessage>
                        </div>
                    )}
                </div>
            </Field>
        </div>
    );
};
